import React from "react";

/**
 * Renders the small markdown subset the model is allowed to use in answers
 * (**bold**, `code`, "- " bullets, "1. " numbered lists, paragraphs) as React
 * nodes. No HTML is ever injected: the answer is model output.
 */

const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`)/g;
const BULLET = /^[-*•]\s+/;
const NUMBERED = /^\d+[.)]\s+/;

const renderInline = (text: string): React.ReactNode[] =>
  text
    .split(INLINE_PATTERN)
    .filter((part) => part.length > 0)
    .map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return <code key={index}>{part.slice(1, -1)}</code>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul" | "ol"; items: string[] };

const toBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  let current: Block | null = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (BULLET.test(line)) {
      const item = line.replace(BULLET, "");
      if (current?.kind === "ul") current.items.push(item);
      else {
        flush();
        current = { kind: "ul", items: [item] };
      }
      continue;
    }
    if (NUMBERED.test(line)) {
      const item = line.replace(NUMBERED, "");
      if (current?.kind === "ol") current.items.push(item);
      else {
        flush();
        current = { kind: "ol", items: [item] };
      }
      continue;
    }
    if (current?.kind === "p") current.lines.push(line);
    else {
      flush();
      current = { kind: "p", lines: [line] };
    }
  }
  flush();
  return blocks;
};

export const MarkdownLite: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => (
  <div className={className}>
    {toBlocks(text).map((block, index) => {
      if (block.kind === "p") {
        return (
          <p key={index}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line)}
              </React.Fragment>
            ))}
          </p>
        );
      }
      const items = block.items.map((item, itemIndex) => (
        <li key={itemIndex}>{renderInline(item)}</li>
      ));
      return block.kind === "ul" ? (
        <ul key={index}>{items}</ul>
      ) : (
        <ol key={index}>{items}</ol>
      );
    })}
  </div>
);
